/**
 * Utilitaires pour la compatibilité des formats de sauvegarde entre modes classique et multigrid
 */

// Types pour les différents formats de sauvegarde existants
export interface ClassicSaveFormat {
  version: string;
  timestamp: string;
  canvas: any; // Configuration du canvas classique
  textElements?: any[];
  logoElements?: any[];
}

export interface MultiGridSaveFormat {
  canvas: {
    width: number;
    height: number;
    allowFullscreenScroll: boolean;
  };
  grids: any[];
  timestamp: string;
}

export interface ConfigProviderFormat {
  main: any;
  multiGrid: any;
}

// Format unifié v3.0.0 pour la compatibilité entre modes
export interface UnifiedSaveFormat {
  version: "3.0.0";
  timestamp: string;
  format: "unified";
  
  // Mode d'origine de la sauvegarde
  sourceMode: "classic" | "multigrid";
  
  // Données pour le mode classique
  classic?: {
    canvas: any;
    textElements?: any[];
    logoElements?: any[];
  };
  
  // Données pour le multigrid
  multiGrid?: {
    canvas: {
      width: number;
      height: number;
      allowFullscreenScroll: boolean;
    };
    grids: any[];
  };
  
  // Métadonnées
  metadata: {
    createdBy: string;
    description?: string;
    totalPixels?: number;
    gridCount?: number;
  };
}

/**
 * Détecte le format d'une configuration importée
 */
export function detectSaveFormat(config: any): "classic" | "multigrid" | "configProvider" | "unified" | "unknown" {
  if (!config || typeof config !== 'object') {
    return "unknown";
  }

  // Format unifié v3.0.0
  if (config.version === "3.0.0" && config.format === "unified") {
    return "unified";
  }

  // Format classique (versions 1.0.0 et 2.0.0+)
  if (config.version && config.canvas && !Array.isArray(config.canvas)) {
    return "classic";
  }

  // Format multigrid
  if (config.canvas && config.grids && Array.isArray(config.grids)) {
    return "multigrid";
  }

  // Format ConfigProvider
  if (config.main || config.multiGrid) {
    return "configProvider";
  }

  return "unknown";
}

/**
 * Convertit une configuration classique vers le format unifié
 */
export function convertClassicToUnified(config: ClassicSaveFormat): UnifiedSaveFormat {
  const totalPixels = config.canvas?.width * config.canvas?.height || 0;
  
  return {
    version: "3.0.0",
    timestamp: config.timestamp || new Date().toISOString(),
    format: "unified",
    sourceMode: "classic",
    
    classic: {
      canvas: config.canvas,
      textElements: config.textElements || [],
      logoElements: config.logoElements || []
    },
    
    metadata: {
      createdBy: `VisualGrid Classic (v${config.version || "1.0.0"})`,
      description: `Configuration classique ${config.canvas?.width}x${config.canvas?.height}`,
      totalPixels,
      gridCount: 1
    }
  };
}

/**
 * Convertit une configuration multigrid vers le format unifié
 */
export function convertMultiGridToUnified(config: MultiGridSaveFormat): UnifiedSaveFormat {
  const totalPixels = config.grids?.reduce((total, grid) => 
    total + (grid.width * grid.height || 0), 0) || 0;
  
  return {
    version: "3.0.0",
    timestamp: config.timestamp || new Date().toISOString(),
    format: "unified",
    sourceMode: "multigrid",
    
    multiGrid: {
      canvas: config.canvas,
      grids: config.grids || []
    },
    
    metadata: {
      createdBy: "VisualGrid MultiGrid",
      description: `Configuration multigrid ${config.canvas?.width}x${config.canvas?.height} avec ${config.grids?.length || 0} grilles`,
      totalPixels,
      gridCount: config.grids?.length || 0
    }
  };
}

/**
 * Convertit le format unifié vers le format classique
 */
export function convertUnifiedToClassic(config: UnifiedSaveFormat): ClassicSaveFormat {
  if (config.classic) {
    return {
      version: "2.0.0",
      timestamp: config.timestamp,
      canvas: config.classic.canvas,
      textElements: config.classic.textElements || [],
      logoElements: config.classic.logoElements || []
    };
  }
  
  // Si c'est un multigrid, créer une configuration classique basique
  if (config.multiGrid) {
    const firstGrid = config.multiGrid.grids[0];
    const canvas = {
      width: firstGrid?.width || config.multiGrid.canvas.width,
      height: firstGrid?.height || config.multiGrid.canvas.height,
      gridSize: firstGrid?.gridSize || 32,
      animationSpeed: firstGrid?.animationSpeed || 0.8,
      sonarSpeed: firstGrid?.sonarSpeed || 0.2,
      showGrid: firstGrid?.showGrid !== undefined ? firstGrid.showGrid : true,
      patternType: firstGrid?.patternType || "solid",
      standardPatternType: firstGrid?.standardPatternType || "none",
      rampDirection: firstGrid?.rampDirection || "horizontal",
      backgroundColor: firstGrid?.backgroundColor || "#000000",
      showResolution: firstGrid?.showResolution !== undefined ? firstGrid.showResolution : true,
      showLEDNumbers: firstGrid?.showLEDNumbers !== undefined ? firstGrid.showLEDNumbers : true,
      showLogo: firstGrid?.showLogo !== undefined ? firstGrid.showLogo : true,
      showSonar: firstGrid?.showSonar !== undefined ? firstGrid.showSonar : false,
      // S'assurer que ledWallMode a une structure complète
      ledWallMode: {
        enabled: firstGrid?.dalleConfigMode ? firstGrid.dalleConfigMode !== "simple" : true,
        columns: Math.ceil((firstGrid?.width || config.multiGrid.canvas.width) / (firstGrid?.dalleWidth || firstGrid?.gridSize || 192)) || 10,
        rows: Math.ceil((firstGrid?.height || config.multiGrid.canvas.height) / (firstGrid?.dalleHeight || firstGrid?.gridSize || 192)) || 6,
        panelWidth: firstGrid?.dalleWidth || firstGrid?.gridSize || 192,
        panelHeight: firstGrid?.dalleHeight || firstGrid?.gridSize || 192,
        borderWidth: firstGrid?.borderWidth || 2
      },
      sonarRevealMode: false,
      allowFullscreenScroll: config.multiGrid.canvas.allowFullscreenScroll || false,
      stretchFullscreen: false,
      gridOffset: {
        x: firstGrid?.x || 0,
        y: firstGrid?.y || 0
      }
    };
    
    return {
      version: "2.0.0",
      timestamp: config.timestamp,
      canvas,
      textElements: firstGrid?.textElements || [],
      logoElements: firstGrid?.logoElements || []
    };
  }
  
  throw new Error("Configuration unifiée invalide pour la conversion classique");
}

/**
 * Convertit le format unifié vers le format multigrid
 */
export function convertUnifiedToMultiGrid(config: UnifiedSaveFormat): MultiGridSaveFormat {
  if (config.multiGrid) {
    return {
      canvas: config.multiGrid.canvas,
      grids: config.multiGrid.grids,
      timestamp: config.timestamp
    };
  }
  
  // Si c'est une configuration classique, créer une grille unique
  if (config.classic) {
    const grid = {
      id: `grid_${Date.now()}`,
      name: "Grille importée",
      x: 0,
      y: 0,
      ...config.classic.canvas,
      textElements: config.classic.textElements || [],
      logoElements: config.classic.logoElements || [],
      activePatternMode: "custom"
    };
    
    return {
      canvas: {
        width: config.classic.canvas.width || 1920,
        height: config.classic.canvas.height || 1080,
        allowFullscreenScroll: false
      },
      grids: [grid],
      timestamp: config.timestamp
    };
  }
  
  throw new Error("Configuration unifiée invalide pour la conversion multigrid");
}

/**
 * Convertit n'importe quel format vers le format unifié
 */
export function convertToUnified(config: any): UnifiedSaveFormat {
  const format = detectSaveFormat(config);
  
  switch (format) {
    case "unified":
      return config as UnifiedSaveFormat;
      
    case "classic":
      return convertClassicToUnified(config as ClassicSaveFormat);
      
    case "multigrid":
      return convertMultiGridToUnified(config as MultiGridSaveFormat);
      
    case "configProvider":
      // Convertir depuis le format ConfigProvider
      if (config.main && !config.multiGrid) {
        // Configuration classique seulement
        const classicConfig: ClassicSaveFormat = {
          version: "2.0.0",
          timestamp: new Date().toISOString(),
          canvas: config.main
        };
        return convertClassicToUnified(classicConfig);
      } else if (config.multiGrid) {
        // Configuration multigrid
        const multiGridConfig: MultiGridSaveFormat = {
          canvas: config.multiGrid,
          grids: [],
          timestamp: new Date().toISOString()
        };
        return convertMultiGridToUnified(multiGridConfig);
      }
      break;
      
    default:
      throw new Error(`Format de sauvegarde non reconnu: ${format}`);
  }
  
  throw new Error("Impossible de convertir la configuration");
}

/**
 * Valide une configuration unifiée
 */
export function validateUnifiedConfig(config: UnifiedSaveFormat): boolean {
  try {
    if (!config || typeof config !== 'object') return false;
    if (config.version !== "3.0.0") return false;
    if (config.format !== "unified") return false;
    if (!config.sourceMode || !["classic", "multigrid"].includes(config.sourceMode)) return false;
    if (!config.timestamp) return false;
    if (!config.metadata) return false;
    
    // Vérifier qu'au moins un mode a des données
    if (!config.classic && !config.multiGrid) return false;
    
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * S'assurer qu'une configuration classique a la structure ledWallMode complète
 */
export function ensureCompleteClassicConfig(config: any): any {
  // Configuration par défaut pour ledWallMode
  const defaultLedWallMode = {
    enabled: true,
    columns: 10,
    rows: 6,
    panelWidth: 192,
    panelHeight: 192,
    borderWidth: 2
  };

  // Configuration par défaut pour gridOffset
  const defaultGridOffset = {
    x: 0,
    y: 0
  };

  // S'assurer que la structure est complète
  const completeConfig = {
    ...config,
    ledWallMode: {
      ...defaultLedWallMode,
      ...(config.ledWallMode || {})
    },
    gridOffset: {
      ...defaultGridOffset,
      ...(config.gridOffset || {})
    },
    // S'assurer que les propriétés essentielles existent (alignées avec defaultMainConfig)
    width: config.width || 1920,
    height: config.height || 1152,
    gridSize: config.gridSize || 192,
    animationSpeed: config.animationSpeed ?? 0.8,
    sonarSpeed: config.sonarSpeed ?? 0.2,
    patternSeed: config.patternSeed ?? 0,
    exportFps: config.exportFps ?? 60,
    exportFormat: config.exportFormat || 'webm',
    showGrid: config.showGrid ?? true,
    gridEvenOpacity: config.gridEvenOpacity ?? (config.gridDarknessGap !== undefined ? 90 : 97),
    gridOddOpacity: config.gridOddOpacity ?? (config.gridDarknessGap !== undefined ? Math.max(0, 90 - (config.gridDarknessGap || 0)) : 85),
    patternType: config.patternType || "gradient",
    standardPatternType: config.standardPatternType || "none",
    rampDirection: config.rampDirection || "horizontal",
    backgroundColor: config.backgroundColor || "#000000",
    showResolution: config.showResolution ?? true,
    showLEDNumbers: config.showLEDNumbers ?? true,
    showLogo: config.showLogo ?? true,
    showSonar: config.showSonar ?? true,
    sonarThickness: config.sonarThickness ?? 4,
    // Options dérivées/unifiées
    showTechnicalDetails:
      config.showTechnicalDetails ?? (
        (config.showGrid ?? true) &&
        (config.showResolution ?? true) &&
        (config.showLEDNumbers ?? true)
      ),
    sonarMode: config.sonarMode ?? false,
    showVisualEffects: config.showVisualEffects ?? true,
    showDefaultLogo: config.showDefaultLogo ?? true,
    sonarRevealMode: config.sonarRevealMode ?? false,
    allowFullscreenScroll: config.allowFullscreenScroll ?? false,
    stretchFullscreen: config.stretchFullscreen ?? false,
    pixelMapMode: {
      enabled: false,
      mainScreenText: 'MAIN SCREEN',
      posX: 0,
      posY: 0,
      resW: 1920,
      resH: 1152,
      panelW: 192,
      panelH: 192,
      size: 1.0,
      ...(config.pixelMapMode || {})
    },
    // Nova Screen Config
    novaScreenConfigMode: config.novaScreenConfigMode ?? false,
    novaShowArrows: config.novaShowArrows ?? true,
    novaShowLines: config.novaShowLines ?? true,
    novaMinimalMode: config.novaMinimalMode ?? false,
    // Anamorphose
    enableAnamorphosis: config.enableAnamorphosis ?? false
  };

  return completeConfig;
}
